import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { RoadPlanService } from '../proxy/operational/controllers';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  RoadPlanCreateDto,
  RoadPlanUpdateDto,
} from '../proxy/operational/dtos/road-plan';
import { ACTION_IDS, ROADPLAN_ACTIONS, ROADPLAN_CONFIG, ROADPLAN_FIELDS } from './road-plan.config';
import {
  BaseCrudComponent,
  createImportExcelModalConfig,
  CrudPage,
  DEFAULT_TOASTER_LIFE,
  ExcelImportErrorsExportLike,
  ExcelImportResultLike,
  GenericForm,
  IActionsSchema,
  ICrudService,
  IMPORT_AGAIN_LABEL,
  IMPORT_MAX_SIZE_MB,
  IMPORT_MODAL_TITLE,
  ModalBuilder,
  PlanTypes,
  resolveActions,
  ZnExcelImportErrorsModal,
  ZnFileUploader,
} from '@zendra/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlanCreationService } from '../services/plan-creation.service';
import {
  RoadPlanPlanningOrchestratorService,
  RoadPlanProjectionPlanningOrchestratorService,
} from 'orchestrator';
import {
  GetRoadPlanProjectionListInputDto,
  RoadPlanProjectionDto,
  RoadPlanProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/road-plans/models';

const IMPORT_TEMPLATE_FILE_NAME = 'RoadPlanImportTemplate.xlsx';

type RoadPlanImportResult = ExcelImportResultLike<unknown> & {
  fileName?: string;
};

@Component({
  selector: 'app-road-plan',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [
    ListService,
    RoadPlanService,
    ConfirmationService,
    PlanCreationService,
  ],
  templateUrl: './road-plan.html',
  styleUrl: './road-plan.scss',
})
export class RoadPlan extends BaseCrudComponent<
  RoadPlanProjectionDto,
  RoadPlanCreateDto,
  RoadPlanUpdateDto,
  RoadPlanProjectionExcelDownloadDto
> implements OnInit {
  private readonly operationalService = inject(RoadPlanService);
  private readonly planCreationService = inject(PlanCreationService);
  private readonly orchestratorService = inject(RoadPlanPlanningOrchestratorService);
  private readonly projectionOrchestratorService = inject(
    RoadPlanProjectionPlanningOrchestratorService,
  );
  private readonly modalService = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);

  service: ICrudService<
    RoadPlanProjectionDto,
    RoadPlanCreateDto,
    RoadPlanUpdateDto,
    RoadPlanProjectionExcelDownloadDto
  > = {
    create: input => this.operationalService.create(input),
    update: (id, input) => this.operationalService.update(id, input),
    deleteByIds: ids => this.operationalService.deleteByIds(ids),
    getList: (query: GetRoadPlanProjectionListInputDto) =>
      this.projectionOrchestratorService.getList(query),
    getDownloadToken: () =>
      this.projectionOrchestratorService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.projectionOrchestratorService.getListAsExcelFile(input),
    projectionConsistency: this.createProjectionConsistency(
      this.projectionOrchestratorService,
      'roadPlanId',
    ),
  };

  config = ROADPLAN_CONFIG;
  fields = ROADPLAN_FIELDS;
  actions: IActionsSchema | null = null;

  private readonly triggerMap: Record<number, () => void> = {
    [ACTION_IDS.importExcelModal]: () => this.openImportModal(),
    [ACTION_IDS.downloadExcelTemplateTrigger]: () =>
      this.downloadExcelTemplate(),
  };

  ngOnInit(): void {
    this.actions = resolveActions(
      ROADPLAN_ACTIONS,
      this.triggerMap,
    );
  }

  override create(): void {
    this.planCreationService.openCreate({
      planType: PlanTypes.Road,
      fields: this.fields,
      form: this.genericForm,
      injector: this.injector,
      destroyRef: this.destroyRef,
      title: `${this.localizationResource.LanguageService}::TableNewRegistry`,
      entityLocaleKey: this.config.localizationKey,
      onResolved: values => {
        this.selectedEntity = values;
      },
    });
  }

  protected prepareUpdateDto(
    data: any,
    selected: RoadPlanProjectionDto,
  ): RoadPlanUpdateDto {
    return {
      planVersion: data.planVersion,
      startDate: data.startDate,
      periodKilometers: data.periodKilometers,
      planTypeId: selected.planTypeId,
      harvestBlockId: data.harvestBlockId,
      versionId: data.versionId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'RoadPlan';
  }

  private openImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.RoadPlan',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importRoadPlan(formData),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: RoadPlanImportResult) => {
        if (result?.hasErrors) {
          this.openImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openImportErrorsModal(
    result: RoadPlanImportResult,
  ): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.RoadPlan',
        confirmLabel: IMPORT_AGAIN_LABEL,
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: IMPORT_MODAL_TITLE,
          subTitle: 'Operational::Entity.RoadPlan',
          exportErrors: (
            input: ExcelImportErrorsExportLike,
          ) =>
            this.orchestratorService.exportRoadPlanImportErrors(
              input,
            ),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openImportModal();
        }
      });
  }

  private downloadExcelTemplate(): void {
    this.orchestratorService
      .getRoadPlanImportTemplate()
      .subscribe({
        next: result => {
          this.toaster.success(
            'LanguageService::Actions:DownloadTemplateMessage',
            '',
            {
              life: DEFAULT_TOASTER_LIFE,
              messageLocalizationParams: [
                this.localizationService.instant(
                  'LanguageService::Actions:ExcelTemplateDownload',
                ),
              ],
            },
          );
          this.downloadFile(
            result,
            IMPORT_TEMPLATE_FILE_NAME,
          );
        },
        error: error => {
          this.toaster.error(error, '', {
            life: DEFAULT_TOASTER_LIFE,
          });
        },
      });
  }
}
